import * as coreModuleApi from "@/core_bridge/module_api";
import { GroupEventSync } from "@/features/groups/GroupEventSync";
import * as db from "./effects/db/storage";
import { openDatabase } from "./effects/db";
import { generateUUID as uuidv4 } from "@/core_bridge/module_api";
import { encryptDMMessageForDevice, encryptDMMessageWithPublicKey, encryptMessage, getRecipientDeviceKeys } from "./encryption";
import { useStore } from "@/state/legacy_module_api";
import { appConfig } from "@/app/module_api";
import { HandleResult } from "@/core_bridge/module_api";
import { setHandlers as setWebSocketHandlers, send as sendWebSocket } from "@/transport/webSocketService";
import { messageReceivedEvent } from "@/transport/websocket_events";
import { extractContactPublicKeys, serializeContactPublicKeys, resolveContactPublicKey } from "@/data/adapters/contact_keys";
import { handleCommunityUpdatedPayload } from "@/features/community/communityService";
import {
  applyIncomingKeySync,
  extractIncomingKeySyncPayload,
  maybeSendLinkedDeviceKeySync,
} from "@/features/more/deviceLinking";
import {
  createDirectThreadKey,
  createGroupThreadKey,
  groupIdFromThreadKey,
  resolveThreadInfo,
} from "@/core_bridge/thread_keys";
import { toast } from "@/state/hooks";
import type { ThreadType as CoreThreadType } from "@/core_bridge/module_api";

export type { ThreadType } from "@/core_bridge/module_api";

const {
  api,
  initSwitchCore,
  ThreadType,
  MessageType,
  MessageStatus,
  decryptGroupMessage,
  parseAttachmentContent,
  parseGiphyContent,
  detectMessageContentType,
  parseMessageContent,
  handleMessageJson,
  handleIncomingMessageJson,
  parseJwtPayload,
  MessageTypeHelper,
  WebSocketEventType,
} = coreModuleApi;
const handleIncomingMessageJsonForDevice =
  (coreModuleApi as any).handleIncomingMessageJsonForDevice || handleIncomingMessageJson;

/**
 * Thread Service (adapter to switch-core)
 * Handles selection, sending, and event fan-out for threads.
 * NOTE: Core FFI is expected to expose thread_* APIs; currently we map to ws_send_raw as a placeholder.
 */

type ThreadEvent =
  | { type: "thread_selected"; threadKey?: string; threadType?: CoreThreadType; withUserId?: string; groupId?: string }
  | { type: "thread_message_appended"; threadKey: string; threadType?: CoreThreadType; message: any }
  | { type: "thread_message_updated"; threadKey: string; messageId: string; updates: any }
  | { type: "thread_snapshot"; threadKey: string; threadType?: CoreThreadType; messages: any[]; hasMore?: boolean }
  | { type: "thread_error"; message: string }
  | { type: "ws_state_changed"; connected: boolean }
  | { type: string; [key: string]: any };

let handlers: {
  onEvent?: (evt: ThreadEvent) => void;
} = {};
let initialized = false;

export function setHandlers(h: typeof handlers) {
  handlers = h;
}

interface ThreadInfo {
  threadKey: string | undefined;
  threadType: CoreThreadType | undefined;
}

function computeThreadInfo(opts: {
  groupId?: string;
  senderId?: string;
  receiverId?: string;
  threadKey?: string;
  threadType?: CoreThreadType;
  withUserId?: string;
}): ThreadInfo {
  return resolveThreadInfo(opts);
}

function resolveCurrentUserId(): string | undefined {
  const store = useStore.getState() as any;
  return (
    store.userId ||
    (store.apiToken ? parseJwtPayload(store.apiToken)?.user_id : undefined)
  );
}

function contactDisplayName(contact: any, fallback: string | undefined): string {
  return (
    contact?.fullHandle ||
    contact?.displayName ||
    contact?.username ||
    fallback ||
    "New message"
  );
}

function showIncomingMessageToast(message: Record<string, any>, options: {
  title?: string | null;
  body?: string | null;
  threadKey?: string | null;
}) {
  if (typeof window === "undefined") return;

  const store = useStore.getState() as any;
  const currentUserId = resolveCurrentUserId();
  if (message.senderId && currentUserId && message.senderId === currentUserId) return;

  const threadKey = options.threadKey || message.threadKey;
  const thread = threadKey ? store.threadMap?.[threadKey] : undefined;
  const sender = message.senderId ? store.contactsById?.[message.senderId] : undefined;
  const title =
    options.title ||
    thread?.name ||
    contactDisplayName(sender, message.senderId);
  const body =
    options.body ||
    message.content ||
    "You have received a message";

  toast.info(title, {
    id: message.id ? `incoming-message-${message.id}` : undefined,
    description: body,
    duration: 5000,
  });
}

function incomingMessageNotificationTitle(message: Record<string, any>, fallback?: string | null): string {
  if (fallback) return fallback;

  const store = useStore.getState() as any;
  const threadKey = message.threadKey;
  const thread = threadKey ? store.threadMap?.[threadKey] : undefined;
  const sender = message.senderId ? store.contactsById?.[message.senderId] : undefined;
  return thread?.name || contactDisplayName(sender, message.senderId);
}

async function showDesktopNotification(message: Record<string, any>, effect?: Record<string, any>) {
  if (typeof window === "undefined" || !("Notification" in window)) return;
  if (Notification.permission !== "granted") return;

  const title = incomingMessageNotificationTitle(message, effect?.title);
  const body = effect?.body || message.content || "You have received a message";
  const tag = message.id ? `incoming-message-${message.id}` : `incoming-message-${Date.now()}`;
  const options: NotificationOptions = {
    body,
    icon: "/app-splash.png",
    badge: "/app-splash.png",
    tag,
  };

  try {
    const registration = await navigator.serviceWorker?.ready;
    if (registration?.showNotification) {
      await registration.showNotification(title, options);
      return;
    }
  } catch (error) {
    console.warn("[threadService] service worker notification failed; falling back to Notification", error);
  }

  try {
    const notification = new Notification(title, options);
    notification.onclick = () => {
      window.focus();
      notification.close();
    };
  } catch (error) {
    console.warn("[threadService] desktop notification failed", error);
  }
}

function totalUnreadCountFromStore(): number {
  const store = useStore.getState() as any;
  return (Object.values(store.threadMap || {}) as any[]).reduce((total: number, thread: any) => {
    const unread = typeof thread?.unread === "number" ? thread.unread : 0;
    const hiloUnread = typeof thread?.hiloMeta?.unread === "number" ? thread.hiloMeta.unread : 0;
    return total + unread + hiloUnread;
  }, 0);
}

async function syncAppBadgeCount() {
  if (typeof navigator === "undefined") return;
  const badgeNavigator = navigator as Navigator & {
    setAppBadge?: (contents?: number) => Promise<void>;
    clearAppBadge?: () => Promise<void>;
  };
  if (!badgeNavigator.setAppBadge && !badgeNavigator.clearAppBadge) return;

  const unreadCount = totalUnreadCountFromStore();
  try {
    if (unreadCount > 0 && badgeNavigator.setAppBadge) {
      await badgeNavigator.setAppBadge(unreadCount);
    } else if (badgeNavigator.clearAppBadge) {
      await badgeNavigator.clearAppBadge();
    }
  } catch (error) {
    console.debug("[threadService] app badge update skipped", error);
  }
}

function isLikelyJsonSystemPayload(value: string): boolean {
  const trimmed = value.trim();
  return trimmed.startsWith("{") || trimmed.startsWith("[");
}

function isSensitiveSystemPayload(value: string): boolean {
  const lower = value.toLowerCase();
  return (
    lower.includes("\"groupkey\"") ||
    lower.includes("\"groupkeys\"") ||
    lower.includes("\"threadkey\"") ||
    lower.includes("\"keyversion\"")
  );
}

function sanitizeSystemMessageContent(
  messageType: string | null,
  contentTypeHint: string | undefined,
  currentContent: string | undefined
): string | undefined {
  const isSystem =
    MessageTypeHelper.isSystemMessage(messageType) ||
    (contentTypeHint || "").toUpperCase() === "SYSTEM";

  if (!isSystem) return currentContent;

  const content = currentContent?.trim() || "";
  const friendly = MessageTypeHelper.getFriendlyContent(messageType);

  if (!content) return friendly || "System update";
  if (isSensitiveSystemPayload(content)) return friendly || "Encryption keys updated";
  if (isLikelyJsonSystemPayload(content)) return friendly || "System update";
  if (content === "[Encrypted Message]") return friendly || "System update";
  return currentContent;
}

function normalizeMembershipStatus(status: unknown): string | undefined {
  if (typeof status === "string") {
    return status.toLowerCase();
  }
  if (status && typeof status === "object" && "status" in (status as Record<string, unknown>)) {
    const value = (status as Record<string, unknown>).status;
    return typeof value === "string" ? value.toLowerCase() : undefined;
  }
  return undefined;
}

function listHasUser(members: unknown, userId: string): boolean {
  if (!Array.isArray(members) || !userId) return false;
  return members.some((member: any) => {
    if (!member) return false;
    if (typeof member === "string") {
      if (member === userId) return true;
      if (member.trim().startsWith("{")) {
        try {
          const parsed = JSON.parse(member);
          const parsedUserId = parsed?.userId || parsed?.userID || parsed?.user_id;
          return parsedUserId === userId;
        } catch {
          return false;
        }
      }
      return false;
    }
    const memberUserId = member.userId || member.userID || member.user_id;
    return memberUserId === userId;
  });
}

function listHasOtherUsers(members: unknown, userId: string): boolean {
  if (!Array.isArray(members) || !userId) return false;
  return members.some((member: any) => {
    if (!member) return false;
    if (typeof member === "string") {
      if (member !== userId && !member.trim().startsWith("{")) return true;
      if (member.trim().startsWith("{")) {
        try {
          const parsed = JSON.parse(member);
          const parsedUserId = parsed?.userId || parsed?.userID || parsed?.user_id;
          return parsedUserId && parsedUserId !== userId;
        } catch {
          return false;
        }
      }
      return false;
    }
    const memberUserId = member.userId || member.userID || member.user_id;
    return !!memberUserId && memberUserId !== userId;
  });
}

function getSenderPublicKey(
  senderId: string,
  store: any,
  senderKeyId?: string
): string | undefined {
  const sender = store.contactsById?.[senderId];
  return resolveContactPublicKey(sender, senderKeyId);
}

function getGroupKey(threadKey: string | undefined, store: any): string | undefined {
  if (!threadKey) return undefined;
  const thread = store.threadMap?.[threadKey];
  if (!thread) return undefined;

  if (thread.groupKeys && thread.currentKeyVersion) {
    return thread.groupKeys[thread.currentKeyVersion];
  }

  if (thread.groupKeys) {
    const versions = Object.keys(thread.groupKeys);
    if (versions.length > 0) {
      const latest = Number(versions.sort().pop());
      return thread.groupKeys[latest];
    }
  }

  return undefined;
}

async function refreshThreadFromStorage(threadKey: string | undefined) {
  if (!threadKey) return;
  try {
    const thread = await db.getGrp(threadKey);
    if (thread) {
      const store = useStore.getState() as any;
      store.setThread?.(thread);
    }
  } catch (err) {
    console.warn("[threadService] failed to refresh thread", err);
  }
}

async function ensureThreadExistsLocally(input: {
  threadKey?: string;
  threadType?: CoreThreadType;
  withUserId?: string | null;
  senderId?: string;
  receiverId?: string;
  groupId?: string;
  createdAt?: number;
}): Promise<void> {
  const threadKey = input.threadKey;
  if (!threadKey) return;

  try {
    const existing = await db.getGrp(threadKey);
    if (existing) return;

    const currentUserId = resolveCurrentUserId();
    const inferredThreadType =
      input.threadType ||
      (threadKey.startsWith("group:")
        ? ThreadType.GROUP
        : input.senderId && input.receiverId && input.senderId === input.receiverId
          ? ThreadType.SELF
          : ThreadType.DM);

    const inferredWithUserId =
      input.withUserId ||
      (inferredThreadType === ThreadType.GROUP
        ? undefined
        : [input.senderId, input.receiverId]
            .find((id) => !!id && id !== currentUserId));

    const store = useStore.getState() as any;
    const contact = inferredWithUserId ? store.contactsById?.[inferredWithUserId] : undefined;
    const now = input.createdAt || Date.now();
    const placeholderThread = {
      id: threadKey,
      name:
        inferredThreadType === ThreadType.GROUP
          ? `Group ${input.groupId || groupIdFromThreadKey(threadKey) || ""}`.trim()
          : contact?.fullHandle || contact?.username || inferredWithUserId || "Direct Message",
      description: null,
      withUserId: inferredThreadType === ThreadType.GROUP ? undefined : inferredWithUserId,
      createdAt: now,
      updatedAt: now,
      unread: 0,
      lastMessage: null,
      isAnnouncementGroup: false,
      isDefaultGroup: false,
      isStandaloneGroup: inferredThreadType === ThreadType.GROUP,
    } as any;

    await (db as any).default.saveThread(placeholderThread);
    store.setThread?.(placeholderThread);
    console.debug("[threadService] re-created missing thread from incoming message", {
      threadKey,
      inferredThreadType,
      inferredWithUserId,
    });
  } catch (err) {
    console.warn("[threadService] failed to ensure local thread exists", {
      threadKey,
      error: err,
    });
  }
}

function normalizeHandlerAction(action: any): { type: string; payload: any } | null {
  if (!action || typeof action !== "object") return null;
  const entries = Object.entries(action);
  if (entries.length !== 1) return null;
  const [type, payload] = entries[0];
  return { type, payload };
}

async function fetchAndStoreContact(userId: string, refresh: boolean) {
  if (!userId) return;
  const store = useStore.getState() as any;
  const existing = store.contactsById?.[userId];
  const existingKeys = extractContactPublicKeys(existing);
  const hasKey = Array.isArray(existingKeys) && existingKeys.some((key: any) =>
    key?.publicKey && key?.deviceId && key.deviceId !== "default"
  );
  if (!refresh && existing && hasKey) return;

  try {
    const response = await api.searchUserId(userId);
    const user = response?.user;
    if (!user) return;

    const rawKeys = user.publicKeys || user.public_keys || [];
    const apiDeviceId =
      user.deviceId ||
      user.device_id ||
      user.primaryDeviceId ||
      user.primary_device_id ||
      (Array.isArray(rawKeys) ? rawKeys[0]?.deviceId || rawKeys[0]?.device_id : undefined) ||
      undefined;
    const normalizedKeys = (Array.isArray(rawKeys) ? rawKeys : [])
      .map((entry: any) => {
        const publicKey = entry?.publicKey ?? entry?.public_key;
        if (!publicKey) return null;
        const deviceId = entry?.deviceId ?? entry?.device_id ?? apiDeviceId;
        return {
          publicKey: String(publicKey).replace(/\s+/g, ""),
          deviceId,
          mlKemPublicKey: entry?.mlKemPublicKey ?? entry?.ml_kem_public_key ?? undefined,
          signingPublicKey: entry?.signingPublicKey ?? entry?.signing_public_key ?? null,
        };
      })
      .filter(Boolean);
    const apiPublicKey = user.publicKey || user.public_key;
    if (!normalizedKeys.length && apiPublicKey) {
      normalizedKeys.push({
        publicKey: String(apiPublicKey).replace(/\s+/g, ""),
        deviceId: apiDeviceId,
        mlKemPublicKey: undefined,
        signingPublicKey: null,
      });
    }
    const serializedKeys = serializeContactPublicKeys(normalizedKeys, apiDeviceId);
    const normalized = {
      ...user,
      userId: user.userId || user.user_id || userId,
      deviceId: apiDeviceId,
      publicKeys: normalizedKeys,
      publicKey: serializedKeys ?? apiPublicKey ?? null,
      isBlocked: false,
      isMuted: false,
    } as any;

    await db.insertUser(normalized);
    store.setUser?.(normalized);
  } catch (err) {
    console.warn("[threadService] failed to fetch contact", userId, err);
  }
}

export async function ensureContactByUserId(userId: string, refresh: boolean = false) {
  await fetchAndStoreContact(userId, refresh);
}

async function resolveGroupMemberPublicKeys(userIds: string[]): Promise<Map<string, string>> {
  const resolved = new Map<string, string>();
  const unique = Array.from(new Set(userIds.filter(Boolean)));
  if (!unique.length) return resolved;

  const store = useStore.getState() as any;

  const applyResults = (results: any[] | null | undefined) => {
    (results || []).forEach((entry: any) => {
      const userId = entry?.userId || entry?.user_id;
      if (!userId || resolved.has(userId)) return;
      const payload =
        entry?.publicKeysJson ??
        entry?.public_keys_json ??
        entry?.publicKey ??
        entry?.public_key ??
        null;
      const keys = extractContactPublicKeys({ publicKey: payload } as any);
      const key =
        keys?.[0]?.publicKey ||
        resolveContactPublicKey(store.contactsById?.[userId], undefined);
      if (key) {
        resolved.set(userId, key);
      }
    });
  };

  const initial = await db.resolvePublicKeys(unique, false);
  applyResults(initial);

  const missing = unique.filter((id) => !resolved.has(id));
  if (missing.length) {
    const refreshed = await db.resolvePublicKeys(missing, true);
    applyResults(refreshed);
  }

  return resolved;
}

async function executeHandleResult(
  result: HandleResult,
  notificationMessage?: Record<string, any>
) {
  const store = useStore.getState() as any;

  for (const action of result.actions || []) {
    const normalized = normalizeHandlerAction(action);
    if (!normalized) continue;

    switch (normalized.type) {
      case "EnsureContactExists": {
        await fetchAndStoreContact(normalized.payload?.user_id, false);
        break;
      }
      case "EnsureThreadExists": {
        const { thread_key, thread_type, with_user_id } = normalized.payload || {};
        await ensureThreadExistsLocally({
          threadKey: thread_key,
          threadType: thread_type,
          withUserId: with_user_id,
        });
        break;
      }
      case "RefreshUserProfile": {
        await fetchAndStoreContact(normalized.payload?.user_id, true);
        break;
      }
      case "StoreThreadKey": {
        const { thread_key, key, key_version } = normalized.payload || {};
        if (!thread_key || !key) break;

        const existing = store.threadMap?.[thread_key];
        if (existing) {
          const nextVersion = key_version ?? existing.currentKeyVersion ?? 1;
          const updatedKeys = { ...(existing.groupKeys || {}) };
          updatedKeys[nextVersion] = key;
          store.updateThread?.(thread_key, {
            groupKeys: updatedKeys,
            currentKeyVersion: nextVersion,
          });
        }
        break;
      }
      case "UpdateThreadMetadata": {
        const { thread_key, name, updated_at } = normalized.payload || {};
        if (!thread_key) break;
        store.updateThread?.(thread_key, {
          name: name ?? undefined,
          updatedAt: updated_at,
        });
        break;
      }
      case "AddThreadMember": {
        const { thread_key, user_id } = normalized.payload || {};
        if (!thread_key || !user_id) break;
        const existing = store.threadMap?.[thread_key];
        if (!existing) break;
        const members = Array.isArray(existing.members) ? [...existing.members] : [];
        if (!members.some((m: any) => m.userId === user_id)) {
          members.push({
            userId: user_id,
            username: user_id,
            role: "member",
            joinedAt: Date.now(),
          });
          store.updateThread?.(thread_key, { members });
        }
        break;
      }
      case "RemoveThreadMember": {
        const { thread_key, user_id } = normalized.payload || {};
        if (!thread_key || !user_id) break;
        const existing = store.threadMap?.[thread_key];
        if (!existing) break;
        const members = Array.isArray(existing.members)
          ? existing.members.filter((m: any) => m.userId !== user_id)
          : [];
        store.updateThread?.(thread_key, { members });
        break;
      }
      case "MarkThreadUnread": {
        // Core decides which incoming messages mark a thread unread; suppress
        // only for the thread the user currently has open (core can't see that).
        const threadId = normalized.payload?.thread_id;
        if (!threadId) break;
        const activeThreadKey = store.selectedThreadId || store.threadSelection?.threadKey;
        if (threadId === activeThreadKey) break;
        await db.markThreadUnread(threadId);
        await refreshThreadFromStorage(threadId);
        await syncAppBadgeCount();
        break;
      }
      case "PersistMessage":
      case "SkipPersist":
        break;
      case "ShowNotification": {
        showIncomingMessageToast(
          {
            ...notificationMessage,
            threadKey:
              normalized.payload?.thread_key ||
              normalized.payload?.thread_id ||
              notificationMessage?.threadKey,
          },
          {
            title: normalized.payload?.title,
            body: normalized.payload?.body,
            threadKey:
              normalized.payload?.thread_key ||
              normalized.payload?.thread_id ||
              notificationMessage?.threadKey,
          }
        );
        break;
      }
      case "LogDebug":
        console.debug(`[core:${normalized.payload?.tag}]`, normalized.payload?.message);
        break;
      case "LogWarning":
        console.warn(`[core:${normalized.payload?.tag}]`, normalized.payload?.message);
        break;
      case "LogError":
        console.error(`[core:${normalized.payload?.tag}]`, normalized.payload?.message);
        break;
      default:
        break;
    }
  }
}

export async function handleIncomingMessage(payload: Record<string, any>) {
  if (!payload?.id) return;
  if (await db.messageExists(payload.id)) return;

  const store = useStore.getState() as any;
  const currentUserId = resolveCurrentUserId();
  const senderId = payload.senderId || payload.sender_id;
  const receiverId = payload.receiverId || payload.receiver_id;
  const groupId = payload.groupId || payload.group_id;
  const groupThreadKey = groupId ? createGroupThreadKey(groupId) : undefined;
  const threadKeyHint = payload.threadKey || payload.thread_key;
  const timestamp = payload.timestamp || Date.now();

  const { threadKey, threadType } = computeThreadInfo({
    groupId,
    senderId,
    receiverId,
    threadKey: threadKeyHint,
  });

  const messageTypeRaw = payload.type || payload.messageType;
  const messageType = MessageTypeHelper.canonicalize(messageTypeRaw) || MessageType.MESSAGE;
  const contentTypeHint = payload.contentType || payload.content_type;
  const replyToMessageId =
    payload.replyToMessageId ||
    payload.replyToMessageID ||
    payload.replyTo ||
    payload.reply_to ||
    payload.reply_to_message_id;
  const hasGroupContext =
    !!groupId ||
    (typeof threadKeyHint === "string" && threadKeyHint.startsWith("group:")) ||
    (typeof threadKey === "string" && threadKey.startsWith("group:"));
  const isThreadLifecycle =
    messageType === MessageType.THREAD_CREATED ||
    messageType === MessageType.THREAD_UPDATED ||
    messageType === MessageType.THREAD_MEMBER_ADDED ||
    messageType === MessageType.THREAD_MEMBER_REMOVED;
  const isGroupReliabilityLifecycle =
    messageType === "GROUP_METADATA_VERSION_CHANGED" ||
    messageType === "GROUP_KEY_EPOCH_CHANGED" ||
    messageType === "GROUP_TOMBSTONE" ||
    messageType === "GROUP_MEMBER_ADDED" ||
    messageType === "GROUP_MEMBER_REMOVED" ||
    messageType === "GROUP_MEMBERSHIP_VERSION_CHANGED" ||
    messageType === "RESYNC_REQUIRED" ||
    messageType === "GROUP_MEMBERSHIP_RESYNC_REQUIRED";
  const isUserAddedLifecycle = messageType === MessageType.USER_ADDED;

  if ((isThreadLifecycle || isGroupReliabilityLifecycle) && !hasGroupContext) {
    await GroupEventSync.recordDmFallbackBlocked(messageType, payload);
    return;
  }

  let decryptedContent: string | undefined;
  let attachment: any | undefined;
  let contentType = contentTypeHint || "TEXT";
  let handleResult: HandleResult | undefined;
  // @platform-only: receive-side plumbing to hold the environment core's
  // envelope unwrap produces (see parseMessageContent / coreResult.message
  // below). The envelope parsing itself lives in core (parse_message_content);
  // this local just carries its output to the message record and notifications.
  let resolvedEnvironment: string | null | undefined;
  let handleActionsExecuted = false;

  if (payload.encryptedContent && payload.nonce) {
    const isGroup = threadType === ThreadType.GROUP;
    if (isGroup) {
      const groupKey = getGroupKey(threadKey || groupThreadKey, store);
      if (groupKey) {
        try {
          const rawGroup = decryptGroupMessage(payload.encryptedContent, payload.nonce, groupKey);
          if (rawGroup) {
            // @platform-only: the group decrypt returns the raw content
            // envelope; core parseMessageContent unwraps it to inner content +
            // environment (with legacy raw-text fallback).
            const parsed = parseMessageContent(rawGroup);
            decryptedContent = parsed.content;
            resolvedEnvironment = parsed.environment;
          }
        } catch (err) {
          console.warn("[threadService] group decrypt failed", err);
        }
      }
    }
    if ((!isGroup || !decryptedContent) && currentUserId) {
      const receiverSecretKey =
        store.keyPair?.base64?.secretKey || store.keyPair?.base64?.secret_key;
      if (receiverSecretKey) {
        try {
          const senderKeyId = payload.senderKeyId || payload.sender_key_id || undefined;
          const senderContact = senderId ? (useStore.getState() as any).contactsById?.[senderId] : undefined;
          const senderPublicKeys = extractContactPublicKeys(senderContact);
          const senderPublicKeyEntry = senderKeyId
            ? senderPublicKeys.find((entry) => entry.deviceId === senderKeyId)
            : senderPublicKeys[0];
          const senderPublicKey = senderId
            ? getSenderPublicKey(
                senderId,
                useStore.getState() as any,
                senderKeyId
              )
            : undefined;
          const receiverDeviceId =
            (store as any).deviceId ||
            (store.apiToken ? parseJwtPayload(store.apiToken)?.device_id : undefined);
          const receiverMlKemPublicKey =
            store.appState?.mlKemPublicKey || store.appState?.ml_kem_public_key || undefined;
          const receiverMlKemSecretKey =
            store.mlKemKeyPair?.base64?.secretKey ||
            (store.mlKemKeyPair as any)?.base64?.secret_key ||
            store.appState?.mlKemSecretKey ||
            store.appState?.ml_kem_secret_key ||
            undefined;
          const corePayload: Record<string, any> = {
            ...payload,
            appId: payload.appId || payload.app_id || appConfig.appId,
            senderId: senderId || payload.senderId || payload.sender_id,
            receiverId: receiverId || payload.receiverId || payload.receiver_id,
            groupId: groupId || payload.groupId || payload.group_id,
            threadKey: threadKey || threadKeyHint || groupThreadKey,
            encryptedContent: payload.encryptedContent || payload.encrypted_content,
            nonce: payload.nonce,
            encVersion: payload.encVersion || payload.enc_version,
            mlKemCiphertext: payload.mlKemCiphertext || payload.ml_kem_ciphertext,
            senderKeyId,
            contentType: payload.contentType || payload.content_type,
            replyTo: replyToMessageId,
            keyVersion: payload.keyVersion || payload.key_version,
            createdAt: payload.createdAt || payload.created_at || payload.timestamp || timestamp,
            updatedAt: payload.updatedAt || payload.updated_at || payload.timestamp || timestamp,
          };
          const isSystemMessage =
            threadType !== ThreadType.GROUP &&
            messageType === MessageType.SYSTEM;
          if (isSystemMessage) {
            console.info("[threadService] decrypting SYSTEM DM", {
              messageId: payload.id || payload.messageId || null,
              senderId: senderId || payload.senderId || payload.sender_id || null,
              receiverId: receiverId || payload.receiverId || payload.receiver_id || null,
              threadKey: threadKey || threadKeyHint || groupThreadKey || null,
              hasReceiverSecretKey: !!receiverSecretKey,
              hasSenderPublicKey: !!senderPublicKey,
              senderPublicKeyPrefix: senderPublicKey?.slice(0, 12) || null,
            });
          }
          let coreResult = handleIncomingMessageJsonForDevice(
            JSON.stringify(corePayload),
            currentUserId,
            receiverSecretKey,
            receiverDeviceId || null,
            receiverMlKemPublicKey || null,
            receiverMlKemSecretKey || null,
            senderPublicKey ?? null,
            senderPublicKeyEntry?.mlKemPublicKey || null
          );
          handleResult = coreResult.handleResult;
          decryptedContent = coreResult.message?.content || undefined;
          // Core already unwrapped the content envelope onto the message:
          // message.content is inner content, message.environment is the
          // encrypted-in HiLo env lifted out of the envelope.
          if (coreResult.message?.environment != null) {
            resolvedEnvironment = coreResult.message.environment as any;
          }

          if (isSystemMessage) {
            console.info("[threadService] SYSTEM DM decrypt result", {
              messageId: payload.id || payload.messageId || null,
              decrypted: !!coreResult.decrypted,
              hasContent: !!decryptedContent,
              actionTypes: (coreResult.handleResult?.actions || []).map((action) => {
                const normalized = normalizeHandlerAction(action as any);
                return normalized?.type || "unknown";
              }),
            });
          }

          if (!coreResult.decrypted) {
            const needsContact = (coreResult.handleResult?.actions || []).some((action) => {
              const normalized = normalizeHandlerAction(action as any);
              return normalized?.type === "EnsureContactExists";
            });

            if (isSystemMessage) {
              console.warn("[threadService] SYSTEM DM not decrypted on first attempt", {
                messageId: payload.id || payload.messageId || null,
                senderId: senderId || payload.senderId || payload.sender_id || null,
                needsContact,
              });
            }

            if (needsContact && senderId) {
              await fetchAndStoreContact(senderId, true);
              await executeHandleResult(coreResult.handleResult);
              handleActionsExecuted = true;
              const refreshedSenderKey = senderId
                ? getSenderPublicKey(
                    senderId,
                    useStore.getState() as any,
                    senderKeyId
                  )
                : undefined;
              if (refreshedSenderKey) {
                const refreshedSenderContact = senderId
                  ? (useStore.getState() as any).contactsById?.[senderId]
                  : undefined;
                const refreshedSenderEntry = extractContactPublicKeys(refreshedSenderContact)
                  .find((entry) => !senderKeyId || entry.deviceId === senderKeyId);
                coreResult = handleIncomingMessageJsonForDevice(
                  JSON.stringify(corePayload),
                  currentUserId,
                  receiverSecretKey,
                  receiverDeviceId || null,
                  receiverMlKemPublicKey || null,
                  receiverMlKemSecretKey || null,
                  refreshedSenderKey,
                  refreshedSenderEntry?.mlKemPublicKey || null
                );
                handleResult = coreResult.handleResult;
                decryptedContent = coreResult.message?.content || decryptedContent;
                if (coreResult.message?.environment != null) {
                  resolvedEnvironment = coreResult.message.environment as any;
                }
                handleActionsExecuted = false;
                if (isSystemMessage) {
                  console.info("[threadService] SYSTEM DM retry decrypt result", {
                    messageId: payload.id || payload.messageId || null,
                    decrypted: !!coreResult.decrypted,
                    hasContent: !!decryptedContent,
                    refreshedSenderPublicKeyPrefix: refreshedSenderKey.slice(0, 12),
                  });
                }
              }
            }
          }
        } catch (err) {
          console.warn("[threadService] core dm decrypt failed", err);
        }
      }
    }
  }

  if (decryptedContent) {
    const keySyncPayload =
      threadType !== ThreadType.GROUP && messageType === MessageType.SYSTEM
        ? extractIncomingKeySyncPayload(decryptedContent)
        : null;
    if (keySyncPayload) {
      console.info("[threadService] received key_sync control payload", {
        publicKeyPrefix: keySyncPayload.publicKey.slice(0, 12),
      });
      await applyIncomingKeySync(keySyncPayload);
      return;
    }

    const attachmentPayload = parseAttachmentContent(decryptedContent);
    if (attachmentPayload) {
      attachment = attachmentPayload;
      contentType = "FILE";
    }

    const giphyPayload = parseGiphyContent(decryptedContent);
    if (giphyPayload) {
      contentType = "GIPHY";
    }

    if (!contentTypeHint && contentType === "TEXT") {
      const detected = detectMessageContentType(decryptedContent, contentTypeHint);
      contentType = detected === "UNKNOWN" ? "TEXT" : detected;
    }
  }

  const message = {
    id: payload.id,
    type: messageType,
    appId: appConfig.appId,
    content: decryptedContent || payload.content || undefined,
    contentType: contentType,
    senderId,
    receiverId,
    threadKey: threadKey || threadKeyHint || groupThreadKey,
    threadType,
    groupId,
    replyToMessageId,
    status: MessageStatus.DELIVERED,
    createdAt: timestamp,
    updatedAt: timestamp,
    encryptedContent: payload.encryptedContent,
    nonce: payload.nonce,
    encVersion: payload.encVersion || payload.enc_version,
    mlKemCiphertext: payload.mlKemCiphertext || payload.ml_kem_ciphertext,
    senderKeyId: payload.senderKeyId || payload.sender_key_id,
    keyVersion: payload.keyVersion || payload.key_version,
    attachment,
    groupKey: payload.groupKey || payload.group_key,
    // Environment recovered from inside the decrypted content envelope
    // (core-unwrapped for DM, parseMessageContent for group). Falls back to the
    // legacy top-level field for pre-envelope messages, where the sender put
    // environment on the wire in plaintext.
    environment: resolvedEnvironment ?? payload.environment,
    errors: payload.errors,
  } as any;

  if (!handleResult && currentUserId) {
    try {
      handleResult = handleMessageJson(JSON.stringify(message), currentUserId);
    } catch (err) {
      console.warn("[threadService] handleMessageJson failed", err);
    }
  }

  if (isThreadLifecycle || isGroupReliabilityLifecycle || (isUserAddedLifecycle && hasGroupContext)) {
    const groupSyncPayload: Record<string, any> = {
      ...payload,
      groupId: groupId || payload.groupId || payload.group_id,
      threadKey: threadKey || threadKeyHint || groupThreadKey,
    };

    if (decryptedContent) {
      try {
        const parsed = JSON.parse(decryptedContent) as Record<string, any>;
        const groupKey = parsed?.groupKey || parsed?.group_key;
        if (groupKey) {
          groupSyncPayload.groupKey = groupKey;
        }
        const groupName = parsed?.groupName || parsed?.group_name;
        if (groupName) {
          groupSyncPayload.groupName = groupName;
        }
        const groupVersion = parsed?.groupVersion ?? parsed?.group_version;
        if (groupVersion !== undefined && groupVersion !== null) {
          groupSyncPayload.groupVersion = groupVersion;
        }
        const membershipVersion = parsed?.membershipVersion ?? parsed?.membership_version;
        if (membershipVersion !== undefined && membershipVersion !== null) {
          groupSyncPayload.membershipVersion = membershipVersion;
        }
        const updatedAt = parsed?.updatedAt ?? parsed?.updated_at;
        if (updatedAt !== undefined && updatedAt !== null) {
          groupSyncPayload.updatedAt = updatedAt;
        }
        const parsedThreadKey = parsed?.threadKey || parsed?.thread_key;
        if (parsedThreadKey) {
          groupSyncPayload.threadKey = parsedThreadKey;
        }
      } catch (err) {
        console.warn("[threadService] failed to parse lifecycle content", err);
      }
    }

    const syncResult = await GroupEventSync.handleEvent(messageType, groupSyncPayload);
    if (!syncResult.shouldPersist) {
      return;
    }
  }

  if (messageType === MessageType.COMMUNITY_UPDATED && decryptedContent) {
    try {
      await handleCommunityUpdatedPayload(
        decryptedContent,
        payload.communityId || payload.community_id
      );
    } catch (err) {
      console.warn("[threadService] failed to handle COMMUNITY_UPDATED", err);
    }
  }

  if (handleResult && !handleActionsExecuted) {
    await executeHandleResult(handleResult, message);
    if (!handleResult.should_persist) return;
    if (handleResult.friendly_content) {
      message.content = handleResult.friendly_content;
    }
  }

  message.content = sanitizeSystemMessageContent(
    messageType,
    contentTypeHint || contentType,
    message.content
  );

  if (handleResult?.should_notify) {
    showIncomingMessageToast(message, {
      body: message.content,
      threadKey: message.threadKey,
    });
  }

  await ensureThreadExistsLocally({
    threadKey: message.threadKey,
    threadType,
    senderId,
    receiverId,
    groupId,
    createdAt: message.createdAt,
  });

  const activeThreadKey = store.selectedThreadId || store.threadSelection?.threadKey;
  const incrementUnread = Boolean(
    handleResult?.should_notify &&
      message.threadKey &&
      message.threadKey !== activeThreadKey &&
      !(message.senderId && currentUserId && message.senderId === currentUserId)
  );

  await db.insertMsg(message, { incrementUnread });
  await refreshThreadFromStorage(message.threadKey);
  if (incrementUnread) {
    await syncAppBadgeCount();
  }
  handlers.onEvent?.({
    type: "thread_message_appended",
    threadKey: message.threadKey,
    threadType,
    message,
  });
}

export async function init() {
  if (initialized) {
    return;
  }
  await initSwitchCore();
  try {
    await openDatabase();
  } catch (err) {
    console.warn("[threadService] failed to open DB", err);
  }
  try {
    await GroupEventSync.runStartupSweep();
  } catch (err) {
    console.warn("[threadService] startup group sync sweep failed", err);
  }
  setWebSocketHandlers({
    onConnect: () => {
      handlers.onEvent?.({ type: "ws_state_changed", connected: true });
      void GroupEventSync.runReconnectSweep().catch((err) => {
        console.warn("[threadService] reconnect group sync sweep failed", err);
      });
    },
    onDisconnect: () => {
      handlers.onEvent?.({ type: "ws_state_changed", connected: false });
    },
    onMessage: (evt) => {
      if (evt?.type === messageReceivedEvent && evt.payload) {
        const payload = evt.payload as Record<string, any>;
        const innerType = (payload.type || payload.messageType || "").toString().toUpperCase();
        if (innerType === MessageType.SYSTEM) {
          console.info("[threadService] websocket SYSTEM message_received", {
            messageId: payload.id || payload.messageId || null,
            senderId: payload.senderId || payload.sender_id || null,
            threadKey: payload.threadKey || payload.thread_key || null,
          });
        }
        if (innerType === "USER_ADDED" || innerType === "COMMUNITY_UPDATED") {
          try {
            (useStore.getState() as any).recordRecoveryReshare?.();
          } catch {
            // ignore: store may not have the action yet in tests
          }
        }
        void handleIncomingMessage(evt.payload as Record<string, any>);
        return;
      }
      if (evt?.type === "system_notification" && evt.payload) {
        console.info("[threadService] websocket system_notification", evt.payload);
        void maybeSendLinkedDeviceKeySync(evt.payload as Record<string, unknown>);
        return;
      }
      if (evt?.type === "recovery_announce" && evt.payload) {
        void (async () => {
          const { handleIncomingRecoveryAnnounce } = await import("@/features/recovery/recoveryAnnounce");
          await handleIncomingRecoveryAnnounce(evt.payload as Record<string, unknown>);
        })();
        return;
      }
      handlers.onEvent?.(evt as ThreadEvent);
    },
  });
  initialized = true;
}

export async function select(payload: {
  threadKey?: string;
  threadType?: CoreThreadType;
  withUserId?: string;
  groupId?: string;
  senderId?: string;
  receiverId?: string;
}) {
  const { threadKey, threadType } = computeThreadInfo(payload);

  handlers.onEvent?.({
    type: "thread_selected",
    ...payload,
    threadKey,
    threadType,
  });

  if (threadKey) {
    try {
      const store = useStore.getState() as any;
      if ((store.environment || store.hilo?.environment) === "hi") {
        await db.resetUnreadHilo(threadKey);
      } else {
        await db.resetUnread(threadKey);
      }
      await refreshThreadFromStorage(threadKey);
      await syncAppBadgeCount();
    } catch (err) {
      console.warn("[threadService] failed to reset unread count", err);
    }
  }

  // Hydrate messages from storage (best-effort)
  try {
    let messages: any[] = [];
    console.log("[threadService] select payload:", {
      threadKey,
      groupId: payload.groupId,
      receiverId: payload.receiverId,
      senderId: payload.senderId,
    });
    if (payload.groupId) {
      console.log("[threadService] fetching by groupId:", payload.groupId);
      messages = (await db.getMessagesByGroupId(payload.groupId)) || [];
    } else if (payload.receiverId && payload.senderId) {
      console.log("[threadService] fetching by sender/receiver:", payload.senderId, payload.receiverId);
      const resolvedThreadId = createDirectThreadKey(payload.senderId, payload.receiverId);
      messages = (await db.getMessagesByThreadId(resolvedThreadId)) || [];
    } else if (threadKey) {
      console.log("[threadService] fetching by threadKey:", threadKey);
      messages = (await db.getMessagesByThreadId(threadKey)) || [];
    }
    console.log("[threadService] hydrated messages", {
      threadKey,
      threadType,
      count: messages.length,
      messageIds: messages.map(m => m.id),
      statuses: messages.map(m => ({ id: m.id, status: m.status })),
    });
    handlers.onEvent?.({
      type: "thread_snapshot",
      threadKey,
      threadType,
      messages,
      hasMore: false,
    });
  } catch (err) {
    console.warn("[threadService] hydration failed", err);
    handlers.onEvent?.({ type: "thread_error", message: String(err) });
  }
}

export async function sendMessage(message: any) {
  const { threadKey, threadType } = computeThreadInfo({
    groupId: message.groupId,
    senderId: message.senderId,
    receiverId: message.receiverId,
    threadKey: message.threadKey,
    threadType: message.threadType,
  });
  if (!threadKey) {
    console.warn("[threadService] sendMessage missing threadKey, aborting");
    return;
  }
  const isGroup = threadType === ThreadType.GROUP || (threadKey && threadKey.startsWith("group:"));
  const msgWithId = {
    ...message,
    id: message.id || uuidv4(),
    threadKey,
    threadType,
    groupId: message.groupId || (isGroup ? groupIdFromThreadKey(threadKey) : undefined),
    receiverId: isGroup ? undefined : message.receiverId,
  };
  const receiverId = isGroup ? undefined : msgWithId.receiverId;

  // Persist unencrypted message locally for display
  try {
    await db.insertMsg(msgWithId);
    console.log("[threadService] saved message to storage", { threadKey, id: msgWithId.id });
    await refreshThreadFromStorage(threadKey);
  } catch (err) {
    console.warn("[threadService] insertMsg failed", err);
  }

  // Emit locally (dedupe by id on the UI side) - show unencrypted for sender
  handlers.onEvent?.({
    type: "thread_message_appended",
    threadKey,
    threadType,
    message: msgWithId,
  });

  // Build minimal payload matching MessageSendPayload schema
  // Only send required fields - no metadata leakage
  const basePayload: Record<string, any> = {
    id: msgWithId.id,
  };
  // Only leak type for CALL messages (required for iOS push notifications)
  if (msgWithId.type === MessageType.CALL) {
    basePayload.type = msgWithId.type;
    console.log("[threadService] adding type CALL to payload");
  }
  if (threadKey) {
    basePayload.threadKey = threadKey;
  }
  if (msgWithId.groupId) {
    basePayload.groupId = msgWithId.groupId;
  }
  if (msgWithId.replyToMessageId) {
    basePayload.replyTo = msgWithId.replyToMessageId;
  }

  // For groups, fan out to all members (excluding sender)
  if (isGroup && threadKey) {
    const store = useStore.getState() as any;
    const currentUserId = resolveCurrentUserId();
    const resolvedGroupId = msgWithId.groupId || groupIdFromThreadKey(threadKey) || threadKey;
    const selectedThread = store.threadMap?.[resolvedGroupId] || store.threadMap?.[threadKey];
    const selectedCommunity = selectedThread?.communityId
      ? store.communityMap?.[selectedThread.communityId]
      : undefined;

    if (selectedThread?.communityId && currentUserId) {
      const membershipError = "You're no longer a member of this community. Rejoin to send messages.";
      const noMembersError = "There are no members in this community yet. Add members before sending messages.";

      const threadStatus = normalizeMembershipStatus(selectedThread.membershipStatus);
      const communityStatus = normalizeMembershipStatus(selectedCommunity?.membershipStatus);
      const threadMembersKnown = Array.isArray(selectedThread.members) && selectedThread.members.length > 0;
      const communityMembersKnown = Array.isArray(selectedCommunity?.members) && selectedCommunity.members.length > 0;

      if (
        threadStatus === "removed" ||
        threadStatus === "exited" ||
        communityStatus === "removed" ||
        communityStatus === "exited" ||
        (threadMembersKnown && !listHasUser(selectedThread.members, currentUserId)) ||
        (communityMembersKnown && !listHasUser(selectedCommunity.members, currentUserId))
      ) {
        console.warn("[threadService] blocked group send:", membershipError, { threadKey });
        handlers.onEvent?.({ type: "thread_error", message: membershipError });
        return;
      }

      if (threadMembersKnown && !listHasUser(selectedThread.members, currentUserId)) {
        console.warn("[threadService] blocked group send:", membershipError, { threadKey });
        handlers.onEvent?.({ type: "thread_error", message: membershipError });
        return;
      }
    }

    const members: { userId: string }[] = selectedThread?.members || [];
    const recipients = Array.from(new Set(members
      .map((m) => m.userId)
      .filter((uid) => Boolean(uid))));

    if (recipients.length === 0) {
      const noMembersError = "There are no members in this community yet. Add members before sending messages.";
      console.warn("[threadService] no group recipients found, skipping send", { threadKey });
      handlers.onEvent?.({ type: "thread_error", message: noMembersError });
      return;
    }

    const publicKeysByUser = await resolveGroupMemberPublicKeys(recipients);
    if (publicKeysByUser.size === 0) {
      console.warn("[threadService] no public keys resolved for group recipients, skipping send");
      return;
    }

    for (const uid of recipients) {
      const contact = store.contactsById?.[uid];
      const receiverDeviceKeys = extractContactPublicKeys(contact);
      if (!receiverDeviceKeys.length) {
        console.warn("[threadService] missing public keys for group member", uid);
        continue;
      }
      for (const receiverKey of receiverDeviceKeys) {
        const encrypted = encryptDMMessageForDevice(
          { ...msgWithId, receiverId: uid },
          receiverKey as any
        );
        if (!encrypted) continue;
        const envelope = {
          type: WebSocketEventType.MESSAGE_SEND,
          payload: {
            ...basePayload,
            encryptedContent: encrypted.encryptedContent,
            nonce: encrypted.nonce,
            encVersion: encrypted.encVersion,
            mlKemCiphertext: encrypted.mlKemCiphertext,
            contentType: encrypted.contentType || msgWithId.contentType,
            receiverId: uid,
            senderKeyId: encrypted.senderKeyId,
            targetDeviceId: encrypted.targetDeviceId,
          },
        };
        try {
          sendWebSocket(envelope.type, envelope.payload);
        } catch (err) {
          console.error("[threadService] sendMessage fanout failed", err);
        }
      }
    }
    return;
  }

  if (threadType === ThreadType.SELF) {
    const encryptedMsg = encryptMessage({
      ...msgWithId,
    });
    const envelope = {
      type: WebSocketEventType.MESSAGE_SEND,
      payload: {
        ...basePayload,
        encryptedContent: encryptedMsg.encryptedContent,
        nonce: encryptedMsg.nonce,
        contentType: encryptedMsg.contentType || msgWithId.contentType,
        receiverId,
      },
    };
    console.log("[threadService] sending self envelope:", JSON.stringify(envelope, null, 2));
    try {
      sendWebSocket(envelope.type, envelope.payload);
    } catch (err) {
      console.error("[threadService] sendMessage failed", err);
      handlers.onEvent?.({ type: "thread_error", message: String(err) });
    }
    return;
  }

  const receiverDeviceKeys = receiverId
    ? getRecipientDeviceKeys(receiverId, useStore.getState().contactsById || {})
    : [];
  if (!receiverDeviceKeys.length) {
    console.warn("[threadService] no recipient device keys resolved", { receiverId });
    handlers.onEvent?.({ type: "thread_error", message: "Recipient has no device keys" });
    return;
  }

  for (const receiverKey of receiverDeviceKeys) {
    const encryptedMsg = encryptDMMessageForDevice(
      { ...msgWithId },
      receiverKey as any
    );
    if (!encryptedMsg) continue;

    const envelope = {
      type: WebSocketEventType.MESSAGE_SEND,
      payload: {
        ...basePayload,
        encryptedContent: encryptedMsg.encryptedContent,
        nonce: encryptedMsg.nonce,
        encVersion: encryptedMsg.encVersion,
        mlKemCiphertext: encryptedMsg.mlKemCiphertext,
        contentType: encryptedMsg.contentType || msgWithId.contentType,
        receiverId,
        senderKeyId: encryptedMsg.senderKeyId,
        targetDeviceId: encryptedMsg.targetDeviceId,
      },
    };
    try {
      sendWebSocket(envelope.type, envelope.payload);
    } catch (err) {
      console.error("[threadService] sendMessage failed", err);
      handlers.onEvent?.({ type: "thread_error", message: String(err) });
    }
  }
}

export function loadOlder(threadKey: string) {
  // TODO: core thread_load_older
  handlers.onEvent?.({ type: "thread_load_older_requested", threadKey } as any);
}

export function markRead(threadKey: string) {
  // TODO: core thread_mark_read
  handlers.onEvent?.({ type: "thread_mark_read", threadKey } as any);
}

export function sendAttachment(_: any) {
  console.warn("[threadService] sendAttachment not implemented");
}
